import React from 'react';

import { Icon } from 'design-system-web';
import { useTranslation } from 'react-i18next';

import {
  GuestCapacityNote as GuestCapacityNoteType,
  GuestCapacityNoteVariantEnum,
} from 'domains-reservable-availability';

import styles from './GuestCapacityNote.scss';

const GUEST_CAPACITY_NOTE_TRANSLATION_KEYS: Record<
  GuestCapacityNoteVariantEnum,
  string
> = {
  [GuestCapacityNoteVariantEnum.NoGuestSpots]:
    'shared.content.feature.reservable.guestCapacity.noGuestSpots',
  [GuestCapacityNoteVariantEnum.SingleGuest]:
    'shared.content.feature.reservable.guestCapacity.singleGuest',
  [GuestCapacityNoteVariantEnum.MultipleGuests]:
    'shared.content.feature.reservable.guestCapacity.multipleGuests',
  [GuestCapacityNoteVariantEnum.UserCapReached]:
    'shared.content.feature.reservable.consecutiveSlot.maxReached',
};

type GuestCapacityNoteProps = {
  /** The note to render, or `null` when there is nothing worth clarifying. */
  note: GuestCapacityNoteType | null;
};

/**
 * Explains how many guests fit in the selected time slot, or warns when the
 * user has reached their consecutive booking limit. Renders nothing when
 * `note` is `null`.
 */
export function GuestCapacityNote({ note }: GuestCapacityNoteProps) {
  const { t } = useTranslation();

  if (!note) {
    return null;
  }

  const isWarning = note.variant === GuestCapacityNoteVariantEnum.UserCapReached;
  const containerClass = isWarning
    ? styles.GuestCapacityNoteWarning
    : styles.GuestCapacityNote;
  const iconName = isWarning ? 'exclamation-triangle' : 'info-circle';

  return (
    <div className={containerClass} data-test="guestCapacityNote">
      <Icon name={iconName} className={styles.icon} size="small" />
      <p className={styles.text}>
        {t(GUEST_CAPACITY_NOTE_TRANSLATION_KEYS[note.variant], {
          guestCount: note.invitableGuests,
        })}
      </p>
    </div>
  );
}
